import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiInstance } from 'services/api'
import { useMasquaradeToken } from 'services/masquarade/masquarade.hooks'
import { useAccessToken } from 'services/token/token.hooks'
import { useTermsOfUse } from 'services/terms/terms.hooks'

export type InsuranceConsentPropertyType = 'residential' | 'hotel' | 'hotel_wis'

export interface InsuranceConsentResponse {
  id: number
  leaseContract: number
  leaseContractNumber: string | null
  propertyType: InsuranceConsentPropertyType
  isConfirmed: boolean
  confirmedAt: string | null
  insuranceAmount: string | null
  statement: string
}

/**
 * Device details sent alongside the consent confirmation. The backend accepts
 * any JSON here and collects IP / User-Agent server-side. MAC address is not
 * available in browsers, so it is intentionally not collected.
 * Keys are camelCase - apiInstance snake-cases the request body
 * (deviceInfo -> device_info, screenResolution -> screen_resolution).
 */
export const getDeviceInfo = () => ({
  browser: navigator.userAgent,
  os: navigator.platform,
  screenResolution: `${window.screen.width}x${window.screen.height}`,
  language: navigator.language,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
})

export const useInsuranceConsentsList = () => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()
  const { hasUserConsented } = useTermsOfUse()

  const {
    data: consentsList = [],
    refetch,
    ...rest
  } = useQuery({
    queryKey: ['insuranceConsents', masquaradeToken],
    // Consent status must always be fresh - never serve stale data.
    cacheTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    // Retry network / server errors only, never 4xx.
    retry: (failureCount, error: any) => {
      const status = error?.response?.status
      if (!!status && status < 500) {
        return false
      }
      return failureCount < 3
    },
    queryFn: () =>
      apiInstance<InsuranceConsentResponse[]>({
        url: '/insurance/consents/',
        method: 'GET',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        }
      }).then(({ data }) => data),
    enabled: !!accessToken && hasUserConsented
  })

  return {
    consentsList,
    refetchConsentsList: refetch,
    ...rest
  }
}

export const useConfirmInsuranceConsent = () => {
  const queryClient = useQueryClient()
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()

  const { mutateAsync: confirmConsent, isLoading: isConfirming } = useMutation({
    mutationFn: (id: number) =>
      apiInstance<InsuranceConsentResponse>({
        url: `/insurance/consents/${id}/confirm/`,
        method: 'POST',
        data: { deviceInfo: getDeviceInfo() },
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        }
      }).then(({ data }) => data),
    onSuccess: (confirmedConsent) => {
      queryClient.setQueryData<InsuranceConsentResponse[]>(
        ['insuranceConsents', masquaradeToken],
        (previousConsents = []) =>
          previousConsents.map((consent) =>
            consent.id === confirmedConsent.id ? confirmedConsent : consent
          )
      )
    }
  })

  return {
    confirmConsent,
    isConfirming
  }
}
